import type { Subscription } from '../../../../../types';

import * as styles from './index.css';
import ActionGroup from '../../../../../layout/forms/action-group';
import BackLink from '../../../../navigation/back-link';
import Box from '@blueshift-ui/core/dist/components/box';
import { EventScopeProvider } from '@blueshift-ui/analytics';
import RevertConfirmation from './revert-confirmation';
import RevertUpdateButton from '../../actions/revert-update';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import SubscriptionDetails from '../subscription-details';
import usePlanPreview from '../../../../../hooks/subscription/use-plan-preview';
import { useState } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface UpdatePendingProps {
  currentSubscription: Subscription;
}

function UpdatePending({ currentSubscription }: UpdatePendingProps) {
  const { translate: RichTranslate } = useTranslation('update_pending', { rich: true });
  const { translate } = useTranslation('update_membership');
  const { isLoading, data: pendingSubscription } = usePlanPreview({
    currentSubscription: currentSubscription,
    previewEndDate: currentSubscription.next_billing_date,
    newSubscription: null,
    scheduledAt: null,
  });
  const [showConfirmation, setShowConfirmation] = useState(false);

  function handleRevertResolved() {
    setShowConfirmation(true);
  }

  if (showConfirmation) {
    return (
      <EventScopeProvider eventScope={{ pageSection: 'revert_pending_membership_confirmation' }}>
        <RevertConfirmation />
      </EventScopeProvider>
    );
  }

  if (isLoading || !pendingSubscription) {
    return null;
  }

  return (
    <>
      <RichTranslation i18nKey="pending_title" translator={RichTranslate} />
      <SubscriptionDetails subscription={pendingSubscription as Subscription} />

      <EventScopeProvider eventScope={{ pageSection: 'revert_pending_membership' }}>
        <Box className={styles.ctaContainer}>
          <RichTranslation i18nKey="previous_title" translator={RichTranslate} />
          <SubscriptionDetails subscription={currentSubscription} />

          <ActionGroup>
            <RevertUpdateButton
              onResolved={handleRevertResolved}
              subscriptionId={currentSubscription.id}
              transformationId={currentSubscription.pending_transformation?.id}
            />

            <BackLink id="back_to_membership_link">{translate('go_back_cta')}</BackLink>
          </ActionGroup>
        </Box>
      </EventScopeProvider>

      <RichTranslation i18nKey="note" translator={RichTranslate} />
    </>
  );
}

export default UpdatePending;
